import { Component, Inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { A11yModule } from '@angular/cdk/a11y';
import { MatAngularModule } from '../../shared/modules/mat-angular.module';
import { ArticleService } from '../../services/article.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

export interface ArticleViewDialogData {
  articleId: string;
}

@Component({
  selector: 'app-article-view-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, MatAngularModule, A11yModule],
  templateUrl: './article-view-dialog.component.html',
  styleUrl: './article-view-dialog.component.scss'
})
export class ArticleViewDialogComponent implements OnInit {
  articleId: string;
  title = '';
  content = '';
  contentHtml: SafeHtml | null = null;
  titleTag = '';
  metaDescription = '';
  seoScore: number | null = null;
  loading = true;
  error: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<ArticleViewDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ArticleViewDialogData,
    private articleService: ArticleService,
    private sanitizer: DomSanitizer,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {
    this.articleId = data.articleId;
  }

  ngOnInit(): void {
    this.articleService.getById(this.articleId).subscribe({
      next: (res) => {
        this.title = res.title || '';
        this.content = res.content || '';
        this.titleTag = res.title_tag || '';
        this.metaDescription = res.meta_description || '';
        this.seoScore = res.seo_score ?? null;
        this.contentHtml = this.sanitizer.bypassSecurityTrustHtml(this.ensureHtml(this.content));
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.error = err.error?.detail || 'Failed to load article';
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  private ensureHtml(text: string): string {
    if (!text) return '';
    // Strip markdown bold (**) so FAQ questions don't show as "**Q: ...**"
    text = text.replace(/\*\*/g, '');
    if (text.trim().startsWith('<')) return text;
    const lines = text.split(/\n/);
    const parts: string[] = [];
    let inList = false;
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();
      if (!trimmed) {
        if (inList) {
          parts.push('</ul>');
          inList = false;
        }
        continue;
      }
      if (/^#+\s/.test(trimmed)) {
        if (inList) {
          parts.push('</ul>');
          inList = false;
        }
        const level = trimmed.match(/^(#+)/)?.[1].length ?? 2;
        const content = trimmed.replace(/^#+\s*/, '');
        parts.push(`<h${Math.min(level, 4)}>${content}</h${Math.min(level, 4)}>`);
      } else if (/^[-*]\s/.test(trimmed) || /^\d+\.\s/.test(trimmed)) {
        if (!inList) {
          parts.push('<ul>');
          inList = true;
        }
        const content = trimmed.replace(/^[-*]\s/, '').replace(/^\d+\.\s/, '');
        parts.push(`<li>${content}</li>`);
      } else {
        if (inList) {
          parts.push('</ul>');
          inList = false;
        }
        parts.push(`<p>${trimmed.replace(/\n/g, '<br>')}</p>`);
      }
    }
    if (inList) parts.push('</ul>');
    return parts.join('\n');
  }

  get titleTagCount(): number {
    return this.titleTag.length;
  }

  get metaDescriptionCount(): number {
    return this.metaDescription.length;
  }

  getFullArticleText(): string {
    const bits: string[] = [this.title, ''];
    if (this.titleTag) bits.push(`Title Tag: ${this.titleTag}`, '');
    if (this.metaDescription) bits.push(`Meta Description: ${this.metaDescription}`, '');
    bits.push(this.content || '');
    return bits.join('\n');
  }

  copyArticle(): void {
    this.copyToClipboard(this.getFullArticleText(), 'Article copied to clipboard');
  }

  private copyToClipboard(text: string, message: string): void {
    if (!text) return;
    navigator.clipboard.writeText(text).then(
      () => this.snackBar.open(message, 'Close', { duration: 2000 }),
      () => {}
    );
  }

  onShare(): void {
    const fullText = this.getFullArticleText();
    if (navigator.share) {
      navigator.share({
        title: this.title,
        text: fullText
      }).catch(() => {});
    } else {
      this.copyToClipboard(fullText, 'Article copied to clipboard');
    }
  }

  download(): void {
    this.articleService.export(this.articleId, 'doc').subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${(this.title || 'article').replace(/[^a-zA-Z0-9-_]/g, '_').slice(0, 60)}.doc`;
        a.click();
        URL.revokeObjectURL(url);
        this.snackBar.open('Download started', 'Close', { duration: 2000 });
      }
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
